import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  IdentificationIcon,
  CubeTransparentIcon,
  RectangleStackIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import {
  getAdvisorProfile,
  listMySkills,
  listMyPlans,
} from "../../api/advisorApi";

// Counts at-a-glance over the advisor's own catalog. Subscriber count is
// stubbed at 0 — Phase 3 wires the marketplace subscription side, Phase 6
// wires aggregated analytics. Until then this card is a placeholder.

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Stat = ({ label, value, hint }) => (
  <div>
    <p className="text-xs uppercase tracking-widest text-gray-500">{label}</p>
    <p className="text-3xl font-bold mt-1">{value}</p>
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const QuickAction = ({ to, icon: Icon, title, desc }) => (
  <Link
    to={to}
    className="group p-5 rounded-2xl border border-gray-200 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] hover:border-indigo-400 dark:hover:border-indigo-500/60 hover:bg-white dark:hover:bg-white/[0.05] transition-colors"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{desc}</p>
      </div>
    </div>
  </Link>
);

const AdvisorDashboard = () => {
  const [advisor, setAdvisor] = useState(null);
  const [skills, setSkills] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, s, p] = await Promise.all([
          getAdvisorProfile(),
          listMySkills(),
          listMyPlans(),
        ]);
        setAdvisor(a);
        setSkills(s || []);
        setPlans(p || []);
      } catch {
        // best-effort — leave stats at zero
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const skillsLive = skills.filter((s) => s.status === "live").length;
  const plansLive = plans.filter((p) => p.review_status === "live").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">
          {loading ? "…" : advisor?.display_name || "Advisor"}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {advisor?.review_status === "approved"
            ? "Your profile is live on the marketplace."
            : "Your profile is in draft. Once you publish a skill and a plan, we'll review and list you."}
        </p>
      </div>

      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          <Stat label="Subscribers" value="0" hint="Aggregate count — names are never shown to you." />
          <Stat label="Skills (live / total)" value={`${skillsLive} / ${skills.length}`} />
          <Stat label="Plans (live / total)" value={`${plansLive} / ${plans.length}`} />
          <Stat label="MRR" value="—" hint="Wired in Phase 5 (Stripe Connect)." />
        </div>
      </Card>

      <div>
        <h2 className="text-lg font-bold mb-3">Quick actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <QuickAction
            to="/advisor/profile"
            icon={IdentificationIcon}
            title="Edit profile"
            desc="Bio, qualifications, intro video, achievements."
          />
          <QuickAction
            to="/advisor/skills"
            icon={CubeTransparentIcon}
            title="Manage skills"
            desc="Author strategies (allocation + AI guidance) the agents will apply."
          />
          <QuickAction
            to="/advisor/plans"
            icon={RectangleStackIcon}
            title="Manage plans"
            desc="Bundle skills, set price, publish for subscriber discovery."
          />
          <QuickAction
            to="/advisor"
            icon={ChartBarIcon}
            title="Analytics"
            desc="Aggregate subscriber / MRR / retention. Wired in Phase 6."
          />
        </div>
      </div>
    </div>
  );
};

export default AdvisorDashboard;
